import * as yup from 'yup';
import dayjs from 'dayjs';
import { getMinAdvanceDaysAhead, getMinTripDuration } from './dateUtils';
import { BUNDLE_TYPES } from '../constants/packagesConstants';

// All below validation functions used `function` over `() =>` as
// we need access to `this` for options & parent.

const { AIR_CAR, AIR_HOTEL_CAR } = BUNDLE_TYPES;

export const validateLocationShape = ({ name, id, type }) => !!(name && id && type);

export function validateDifferentDepartureAndArrival({ id: endId } = {}) {
  const { startLocation: { id: startId } = {} } = this.parent;
  return !startId || !endId || (startId !== endId);
}

export function validateMaximumAdvanceBooking(startDate) {
  const { MAX_ADVANCE_BOOKING } = this.options.marketConfig;
  const startTime = dayjs(startDate);
  const now = dayjs().startOf('day');
  const diff = startTime.diff(now, 'day');
  return diff <= MAX_ADVANCE_BOOKING;
}

export function validateMinimumNights(minTripDuration) {
  return function validateMinimumNightsForLOB(endDate) {
    const startDate = dayjs(this.parent.startDate);
    const timeEnd = dayjs(endDate);
    const diff = timeEnd.diff(startDate, 'day', true);
    return Math.ceil(diff) >= minTripDuration;
  };
}

export function validateMaximumNights(endDate) {
  const { MAX_NIGHTS } = this.options.marketConfig;
  const startDate = dayjs(this.parent.startDate);
  const timeEnd = dayjs(endDate);
  const diff = timeEnd.diff(startDate, 'day');
  return diff <= MAX_NIGHTS;
}

export function validateMaxTravelersPerRoom(rooms = []) {
  const { MAX_TRAVELERS_PER_ROOM } = this.options.marketConfig;
  // eslint-disable-next-line no-restricted-syntax -- Ignore
  for (const { adults, children = [], infants = [] } of rooms) {
    if (adults + children.length + infants.length > MAX_TRAVELERS_PER_ROOM) {
      return false;
    }
  }
  return true;
}

export function validateTotalTravelerCount(rooms = []) {
  const { MAX_TRAVELERS_COUNT } = this.options.marketConfig;
  let travelers = 0;
  rooms.forEach(({
    adults = 0,
    children = [],
    infants = [],
  }) => {
    travelers += adults;
    travelers += children.length;
    travelers += infants.length;
  });
  return travelers <= MAX_TRAVELERS_COUNT;
}

export const validateMinDateByBundleType = (marketConfig) => (bundleType, schema) => {
  const minDaysAhead = getMinAdvanceDaysAhead(bundleType, {
    minAdvanceAirCarBooking: marketConfig.MIN_ADVANCE_AIR_CAR_BOOKING,
    minAdvanceAirHotelBooking: marketConfig.MIN_ADVANCE_AIR_HOTEL_BOOKING,
    minAdvanceAirHotelCarBooking: marketConfig.MIN_ADVANCE_AIR_HOTEL_CAR_BOOKING,
  });
  return schema.min(dayjs().startOf('day').add(minDaysAhead, 'days'), {
    key: 'we_cannot_book_X_days_less_than',
    value: minDaysAhead,
  });
};

export const validateMinTripDuration = (marketConfig) => (bundleType, schema) => {
  const minTripDuration = getMinTripDuration(bundleType, {
    minTripDurationDefault: marketConfig.MIN_NIGHTS,
    minTripDurationAirCar: marketConfig.MIN_NIGHTS_AIR_CAR,
  });
  return schema.test('validate minimum X nights', 'return_date_is_same_as_departing_date', validateMinimumNights(minTripDuration));
};

export function validateInfants(rooms = []) {
  return !rooms.some(({
    adults = 0,
    infants = [],
  }) => {
    let inLapCount = 0;
    infants.forEach((infant) => {
      if (infant === 'inLap') {
        inLapCount += 1;
      }
    });
    return adults < inLapCount;
  });
}

export function validateRequiredAge(age) {
  const { IS_DRIVERS_AGE } = this.options.marketConfig;
  if (IS_DRIVERS_AGE) {
    return !!age && age.trim() !== '';
  }
  return true;
}

export function validateAgeInRange(age) {
  const { IS_DRIVERS_AGE, MIN_DRIVERS_AGE, MAX_DRIVERS_AGE } = this.options.marketConfig;
  if (IS_DRIVERS_AGE) {
    const numberFormattedAge = Number(age);
    return age === '' || (
      Number.isInteger(numberFormattedAge)
      && (numberFormattedAge >= MIN_DRIVERS_AGE && numberFormattedAge <= MAX_DRIVERS_AGE)
    );
  }
  return true;
}

export const validationSchemaWithMarketConfig = (marketConfig) => yup.object().shape({
  startLocation: yup
    .object()
    .test('validate location shape', 'departing_city_airport_required', validateLocationShape),
  endLocation: yup
    .object()
    .test('validate location shape', 'arriving_city_airport_required', validateLocationShape)
    .test('validate different departure and arrival', 'same_arrival_and_departure_location', validateDifferentDepartureAndArrival),
  startDate: yup
    .date()
    .typeError('departing_date_required')
    .min(dayjs().startOf('day'), 'departing_date_has_passed')
    .when('bundleType', validateMinDateByBundleType(marketConfig))
    .test('validate maximum advance booking less than X days', {
      key: 'we_cannot_book_X_days_more_than',
      value: marketConfig.MAX_ADVANCE_BOOKING,
    }, validateMaximumAdvanceBooking),
  endDate: yup
    .date()
    .typeError('return_date_required')
    .min(yup.ref('startDate'), 'return_date_is_same_as_departing_date')
    .min(dayjs().startOf('day'), 'return_date_has_passed')
    .when('bundleType', validateMinTripDuration(marketConfig))
    .test('validate maximum of X nights', {
      key: 'we_cannot_book_stays_longer_than_X_days',
      value: marketConfig.MAX_NIGHTS,
    }, validateMaximumNights),
  roomsTravelers: yup
    .array()
    .test('validate total travelers per room is less than X', {
      key: 'you_have_selected_more_than_X_room',
      value: marketConfig.MAX_TRAVELERS_PER_ROOM,
    }, validateMaxTravelersPerRoom)
    .test('validate total travelers count is less than X', {
      key: 'you_have_selected_more_than_X_total',
      value: marketConfig.MAX_TRAVELERS_COUNT,
    }, validateTotalTravelerCount)
    .test('enough adults for lap infants', 'more_lap_infants_than_adults', validateInfants),
  driversAge: yup
    .string()
    .when(['bundleType', 'isDriversAgeWithinRange'], {
      is: (bundleType, isDriversAgeWithinRange) => [AIR_CAR, AIR_HOTEL_CAR].includes(bundleType)
        && isDriversAgeWithinRange === false,
      then: yup.string()
        .test('validate required age', 'please_enter_drivers_age', validateRequiredAge)
        .test('validate age is within range', 'drivers_age_range', validateAgeInRange),
      otherwise: yup.string(),
    }),
});
